import { useCallback, useMemo, useState } from "react";

const defaultResolveSrc = (sourceKey: string) => sourceKey;

export function normalizeImageSourceKey(sourceKey: string | null | undefined): string | null {
	if (typeof sourceKey !== "string") return null;
	const trimmed = sourceKey.trim();
	return trimmed.length > 0 ? trimmed : null;
}

export type UseImageSrcWithFallbackOptions = {
	sourceKey: string | null | undefined;
	resolveSrc?: (sourceKey: string) => string;
	fallbackSrc?: string | null;
};

export type UseImageSrcWithFallbackResult = {
	imgSrc: string | null;
	onImgError: () => void;
	primarySrc: string | null;
	sourceKeyNormalized: string | null;
};

export function useImageSrcWithFallback({
	sourceKey,
	resolveSrc = defaultResolveSrc,
	fallbackSrc,
}: UseImageSrcWithFallbackOptions): UseImageSrcWithFallbackResult {
	const sourceKeyNormalized = useMemo(() => normalizeImageSourceKey(sourceKey), [sourceKey]);

	const primarySrc = useMemo(
		() => (sourceKeyNormalized ? resolveSrc(sourceKeyNormalized) : null),
		[sourceKeyNormalized, resolveSrc]
	);

	const [failedSourceKey, setFailedSourceKey] = useState<string | null>(null);

	const imgSrc =
		primarySrc && failedSourceKey !== sourceKeyNormalized ? primarySrc : (fallbackSrc ?? null);

	const onImgError = useCallback(() => {
		if (sourceKeyNormalized) setFailedSourceKey(sourceKeyNormalized);
	}, [sourceKeyNormalized]);

	return { imgSrc, onImgError, primarySrc, sourceKeyNormalized };
}
